import { isApiError } from '../api/errors.ts';
import { api, setTokenProvider } from '../api/instance.ts';
import type { DeleteAccountResult, Me } from '../api/types.ts';
import { config } from '../config.ts';
import type { AccountService, OAuthProvider } from '../services/account.ts';
import { db } from '../storage/db.ts';
import { mintDevToken } from './devToken.ts';
import { type Session, useAccount } from './store.ts';
import { supabase } from './supabase.ts';

const DEV_KEY = 'better-lights.devSession.v1';
const ME_KEY = 'account.me';
const SCOPE_KEY = 'account.activeScope';

interface DevSession {
  token: string;
  userId: string;
  email: string;
  expiresAt: number;
}

function readDev(): DevSession | null {
  try {
    const raw = localStorage.getItem(DEV_KEY);
    const s = raw ? (JSON.parse(raw) as DevSession) : null;
    return s && s.expiresAt > Date.now() + 60_000 ? s : null;
  } catch {
    return null;
  }
}

export class RemoteAccountService implements AccountService {
  private dev: DevSession | null = null;
  private readonly signedInListeners = new Set<(first: boolean) => void>();
  private readonly signedOutListeners = new Set<() => void>();
  private refreshTimer: ReturnType<typeof setTimeout> | undefined;

  async init(): Promise<void> {
    setTokenProvider((o) => this.token(o));
    if (config.devAuth) this.dev = readDev();
    let session: Session | null = this.dev ? { kind: 'dev', userId: this.dev.userId, email: this.dev.email } : null;
    const sb = supabase();
    if (!session && sb) {
      const client = await sb;
      const { data } = await client.auth.getSession();
      if (data.session)
        session = { kind: 'supabase', userId: data.session.user.id, email: data.session.user.email ?? null };
      client.auth.onAuthStateChange((event, s) => {
        if (event === 'SIGNED_IN' && s && useAccount.getState().session?.userId !== s.user.id) {
          void this.started({ kind: 'supabase', userId: s.user.id, email: s.user.email ?? null }, true);
        } else if (event === 'SIGNED_OUT' && useAccount.getState().session?.kind === 'supabase') {
          void this.ended();
        }
      });
    }
    if (session) {
      const cached = (await db().kv.get(`${ME_KEY}.${session.userId}`))?.value as
        | { me: Me; fetchedAt: number }
        | undefined;
      const scope = (await db().kv.get(`${SCOPE_KEY}.${session.userId}`))?.value as string | undefined;
      useAccount.setState({
        session,
        me: cached?.me ?? null,
        meFetchedAt: cached?.fetchedAt ?? null,
        activeScope: scope ?? cached?.me.personalScope ?? null,
        ready: true,
      });
      void this.refreshMe().then(() => this.emitSignedIn(false));
    } else useAccount.setState({ ready: true });
    addEventListener('visibilitychange', () => {
      const me = useAccount.getState().me;
      if (
        document.visibilityState === 'visible' &&
        useAccount.getState().session &&
        (!me || Date.parse(me.refreshAfter) < Date.now())
      )
        void this.refreshMe();
    });
  }

  async token(opts?: { forceRefresh?: boolean }): Promise<string | null> {
    const s = useAccount.getState().session;
    if (!s) return null;
    if (s.kind === 'dev') {
      if (!this.dev || this.dev.expiresAt < Date.now() + 60_000)
        await this.devSignIn(s.email ?? 'dev@example.com', false);
      return this.dev?.token ?? null;
    }
    const sb = supabase();
    if (!sb) return null;
    const client = await sb;
    if (opts?.forceRefresh) {
      const { data } = await client.auth.refreshSession();
      return data.session?.access_token ?? null;
    }
    const { data } = await client.auth.getSession();
    return data.session?.access_token ?? null;
  }

  async signInWithProvider(provider: OAuthProvider): Promise<void> {
    const sb = supabase();
    if (!sb) throw new Error('Sign-in isn’t configured for this build.');
    const { error } = await (await sb).auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${location.origin}/account` },
    });
    if (error) throw error;
  }

  async sendEmailCode(email: string): Promise<void> {
    const sb = supabase();
    if (!sb) throw new Error('Sign-in isn’t configured for this build.');
    const { error } = await (await sb).auth.signInWithOtp({ email: email.trim(), options: { shouldCreateUser: true } });
    if (error) throw error;
  }

  async verifyEmailCode(email: string, code: string): Promise<void> {
    const sb = supabase();
    if (!sb) throw new Error('Sign-in isn’t configured for this build.');
    const { data, error } = await (await sb).auth.verifyOtp({ email: email.trim(), token: code.trim(), type: 'email' });
    if (error) throw error;
    if (data.session)
      await this.started(
        { kind: 'supabase', userId: data.session.user.id, email: data.session.user.email ?? null },
        true,
      );
  }

  async devSignIn(email: string, announce = true): Promise<void> {
    if (!config.devAuth) throw new Error('Dev sign-in is disabled in this build.');
    const t = await mintDevToken(email, config.devAuthSecret);
    this.dev = { token: t.token, userId: t.userId, email: email.trim().toLowerCase(), expiresAt: t.expiresAt };
    localStorage.setItem(DEV_KEY, JSON.stringify(this.dev));
    if (announce) await this.started({ kind: 'dev', userId: t.userId, email: this.dev.email }, true);
  }

  async signOut(): Promise<void> {
    const s = useAccount.getState().session;
    if (s?.kind === 'supabase') await (await supabase())?.auth.signOut();
    await this.ended();
  }

  async deleteAccount(): Promise<DeleteAccountResult> {
    const result = await api.deleteMe();
    const userId = useAccount.getState().session?.userId;
    if (userId) await db().kv.bulkDelete([`${ME_KEY}.${userId}`, `${SCOPE_KEY}.${userId}`]);
    await this.signOut();
    return result;
  }

  async refreshMe(): Promise<Me | null> {
    const s = useAccount.getState().session;
    if (!s) return null;
    try {
      const me = await api.me();
      const now = Date.now();
      const st = useAccount.getState();
      const valid = (scope: string | null) =>
        !!scope && (scope === me.personalScope || me.orgs.some((o) => o.id === scope));
      useAccount.setState({
        me,
        meFetchedAt: now,
        offline: false,
        meError: null,
        activeScope: valid(st.activeScope) ? st.activeScope : me.personalScope,
      });
      await db().kv.put({ key: `${ME_KEY}.${s.userId}`, value: { me, fetchedAt: now } });
      clearTimeout(this.refreshTimer);
      const wait = Math.max(30_000, Math.min(30 * 60_000, Date.parse(me.refreshAfter) - now));
      this.refreshTimer = setTimeout(() => void this.refreshMe(), wait);
      return me;
    } catch (e) {
      // Only network failures mean "offline"; a refusal from the server is shown as-is.
      if (isApiError(e) && !e.isNetwork) {
        useAccount.setState({ offline: false, meError: { status: e.status, code: e.code, message: e.message } });
      } else {
        useAccount.setState({ offline: true, meError: null });
      }
      return useAccount.getState().me;
    }
  }

  setActiveScope(scope: string): void {
    useAccount.setState({ activeScope: scope });
    const userId = useAccount.getState().session?.userId;
    if (userId) void db().kv.put({ key: `${SCOPE_KEY}.${userId}`, value: scope });
  }

  onSignedIn(listener: (firstTime: boolean) => void): () => void {
    this.signedInListeners.add(listener);
    return () => this.signedInListeners.delete(listener);
  }

  onSignedOut(listener: () => void): () => void {
    this.signedOutListeners.add(listener);
    return () => this.signedOutListeners.delete(listener);
  }

  private emitSignedIn(first: boolean): void {
    for (const l of this.signedInListeners) l(first);
  }

  private async started(session: Session, first: boolean): Promise<void> {
    useAccount.setState({ session, me: null, meFetchedAt: null, activeScope: null });
    await this.refreshMe();
    this.emitSignedIn(first);
  }

  private async ended(): Promise<void> {
    clearTimeout(this.refreshTimer);
    this.dev = null;
    localStorage.removeItem(DEV_KEY);
    useAccount.setState({
      session: null,
      me: null,
      meFetchedAt: null,
      offline: false,
      meError: null,
      activeScope: null,
    });
    for (const l of this.signedOutListeners) l();
  }
}
